import html2canvas from "html2canvas";
import React from "react";

import { useLocation } from "../../contexts/LocationContext";
import { useRace } from "../../contexts/RaceContext";
import { useYear } from "../../contexts/YearContext";
import { getData, getYears } from "../../data";
import { colors } from "../../utils/constants";
import Card from "../Card";
import LinearChart from "../LinearChart";
import SelectLocation from "../SelectLocation";
import SelectRace from "../SelectRace";
import SelectYear from "../SelectYear";
import styles from "./LinearGraphic.module.css";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function stringToDivs(string) {
  return string.split(" ").map((w) => <div>{w}</div>);
}

function getTemplates() {
  const datasetTemplateCurrent = {
    borderColor: colors.spiroDiscoBall,
    backgroundColor: colors.spiroDiscoBall,
    hoverBorderColor: colors.spiroDiscoBall,
    hoverBackgroundColor: colors.spiroDiscoBall,
  };

  const datasetTemplatePrevious = {
    borderColor: colors.lightSilver,
    backgroundColor: colors.lightSilver,
    hoverBorderColor: colors.spiroDiscoBall,
    hoverBackgroundColor: colors.spiroDiscoBall,
  };

  return {
    datasetTemplateCurrent,
    datasetTemplatePrevious,
  };
}

function getIncremental(arr) {
  const response = [];

  for (let i = 0; i < arr.length; i++) {
    if (i == 0) {
      response[i] = arr[i];
    } else {
      response[i] = response[i - 1] + arr[i];
    }
  }

  return response;
}

function getDataset(y, data, currentYear, currentMonth) {
  const { datasetTemplateCurrent, datasetTemplatePrevious } = getTemplates();
  const response = new Array(currentMonth).fill(0);

  const yearDeaths = data.filter(
    (d) =>
      new Date(d.date).getFullYear() == y &&
      new Date(d.date).getMonth() <= currentMonth
  );

  yearDeaths.forEach((yd) => {
    response[parseInt(yd.date.split("/")[0])] += 1;
  });

  const template =
    currentYear == y ? datasetTemplateCurrent : datasetTemplatePrevious;

  return {
    label: y,
    data: getIncremental(response),
    ...template,
  };
}

function getLabels(datasets, currentYear, currentMonth) {
  let killings = 0;
  let label = "";
  let count = 0;

  const current = datasets.find((d) => d.label == currentYear);
  let last = datasets.find((d) => d.label == currentYear - 1);

  if (!last) {
    last = current;
  }

  const currentDeaths = current.data[currentMonth - 1];
  const lastDeaths = last.data[currentMonth - 1];

  killings = currentDeaths;
  if (currentDeaths > lastDeaths) {
    label = "more";
    count = currentDeaths - lastDeaths;
  } else {
    label = "fewer";
    count = lastDeaths - currentDeaths;
  }

  return {
    label,
    count,
    killings,
  };
}

function getChartInfo(race, year, location) {
  const years = getYears();
  let data = getData();

  if (race !== "all") {
    data = data.filter((d) => d.race == race);
  }

  if (location !== "US") {
    data = data.filter((d) => d.state == location);
  }

  // const currentMonth = new Date().getMonth() + 1;
  const currentMonth = 12;
  const currentMonthLabel = MONTHS[currentMonth - 1];

  const datasets = years.map((y) =>
    getDataset(parseInt(y), data, year, currentMonth)
  );

  const { label, count, killings } = getLabels(datasets, year, currentMonth);

  return { datasets, label, count, killings, currentMonthLabel };
}

const LinearGraphic = () => {
  const printRef = React.useRef();

  const handleDownloadImage = async () => {
    const element = printRef.current;
    const canvas = await html2canvas(element);

    const data = canvas.toDataURL("image/jpg");
    const link = document.createElement("a");

    if (typeof link.download === "string") {
      link.href = data;
      link.download = "image.jpg";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(data);
    }
  };

  const downLoadImage = async () => {
    await handleDownloadImage();
  };

  const race = useRace();
  const year = useYear();
  const location = useLocation();

  const { datasets, label, count, killings, currentMonthLabel } = getChartInfo(
    race,
    year,
    location
  );

  return (
    <div className={styles.container} ref={printRef}>
      <Card
        downLoadImage={downLoadImage}
        message={`Police have killed ${count} ${label} ${race} in ${location} through ${currentMonthLabel} ${year} compared to the same period in the previous year.`}
      >
        <div className={styles.content}>
          <div className={styles.text}>
            <div className={styles.text}>
              {stringToDivs(`Police have killed ${count} ${label}`)}
              <div className={styles.filter}>
                <SelectRace withLabel={false} small={true} />
              </div>
            </div>
            <div>in</div>
            <div className={styles.filter}>
              <SelectLocation withLabel={false} small={true} />
            </div>
            {stringToDivs(`through ${currentMonthLabel}`)}
            <div className={styles.filter}>
              <SelectYear withLabel={false} small={true} />
            </div>
            {stringToDivs("compared to the same period in the previous year.")}
          </div>
          <div>
            <div className={styles.title}>
              <div className={styles.childBottom}>
                {killings} total killings by police
              </div>
            </div>
            <div>
              <LinearChart datasets={datasets} />
            </div>
            <div className={styles.footer}></div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LinearGraphic;
